import { Component, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Business } from '../../../../../core/models/mysec/business';
import { BusinessesService } from '../../../../../core/services/api/mysec/businesses.service';
import { AccountService } from '../../../../../core/services/api/account.service';
import { DefaultLogo } from '../../../../../core/services/constant';
import { ErrorService } from '../../../../../core/services/error.service';
import { BusinessesFilter } from '../../../businesses/businesses-filter';
import { ShareMarketStatus } from '../../../../../core/enum/mysec/share-market-status.enum';
import { ShareStatus } from '../../../../../core/enum/mysec/share-status.enum';
import { GeneralDocumentUploadComponent } from './general-document-upload/general-document-upload.component';
import { SuccessPopupComponent } from './SuccessPopup/success-popup.component';
import { Attachment } from '../../../../../core/models/attachment/attachment';
import { TargetAudience } from '../../../../../core/enum/target-audience.enum';
import { PostNewAnnouncementComponent } from './post-new-announcement/post-new-announcement.component';
import { SettingService } from '../../../../../core/services/api/setting.service';
import { AttachmentType } from '../../../../../core/models/attachment/attachment-type';
import { AttachmentAudience } from '../../../../../core/enum/attachment-audience.enum';

export enum CompanyAttachmentStatus {
  PendingApproval = 1,
  Approved = 2,
  Rejected = 3,
  NotApplicable = 4,
}

@Component({
    selector: 'app-ticker-code',
    templateUrl: './ticker-code.component.html',
    styleUrls: ['./ticker-code.component.scss'],
    standalone: false
})
export class TickerCodeComponent implements OnInit {
  documentsList: any[] = [];
  announcementsList: any[] = [];
  docPage = 1;
  docPageSize = 5;
  thisCompanyId?: number;

  getStatusLabel(status: number) {
    if (!status) return '-';
    switch (status) {
      case 1:
        return 'Pending Review';
      case 2:
        return 'Approved';
      case 3:
        return 'Rejected';
      case 4:
        return 'Not Applicable';
      default:
        return '-';
    }
  }

  getStatusClass(status: number): string {
    const s = Number(status);
    switch (s) {
      case 1:
      case 4:
        return 'tw-bg-divider tw-text-body';

      case 2:
        return 'tw-bg-secondary-lightest tw-text-secondary';

      case 3:
        return 'tw-bg-primary-light tw-text-primary';

      default:
        return 'tw-bg-divider tw-text-body';
    }
  }

  displayColumns: string[] = [
    'Code',
    'DirectShare',
    'NomineeShare',
    'LastTrade',
    'BestBid',
    'BestAsk',
    'OrderQty',
    'PriceChg',
  ];
  total?: number;
  isLoading = false;
  params = {
    ...new BusinessesFilter(),
    // Notes: set this `take` to bigger value to `disable` pagination
    take: 500,
  };
  businessesList?: Business[];
  companyId?: number;
  announcementTotal: number | undefined;
  announcementParams = {
    currentPage: 1,
    take: 5,
  };
  defaultLogo = DefaultLogo;

  totalShares?: number;
  totalNomineeShares?: number;
  nomineeSharesPercentage?: string;

  ShareMarketStatus: typeof ShareMarketStatus = ShareMarketStatus;
  ShareStatus: typeof ShareStatus = ShareStatus;

  public AttachmentStatus = CompanyAttachmentStatus;
  mysecAttachmentTypes: AttachmentType[] = [];

  constructor(
    private errorService: ErrorService,
    private businessesService: BusinessesService,
    private modalService: NgbModal,
    private accountService: AccountService,
    private settingService: SettingService,
  ) {}

  ngOnInit() {
    this.getTickerCodeList();
    this.loadAttachmentTypes();
    this.accountService.getCompanyInfo().subscribe({
      next: (company) => {
        if (company && company.id) {
          this.thisCompanyId = company.id;
          this.getGeneralDocuments();
          this.getAnnouncementList();
        }
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  loadAttachmentTypes() {
    this.settingService.getMysecAttachmentTypes().subscribe({
      next: (types) => {
        this.mysecAttachmentTypes = types || [];
      },
      error: (err) => {
        this.errorService.showError(err);
        this.mysecAttachmentTypes = [];
      },
    });
  }

  getTickerCodeList(reload?: boolean) {
    if (reload) {
      this.params.currentPage = 1;
    }

    this.isLoading = true;

    this.businessesService
      .getTickerCodeList(this.params)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.businessesList = data.data;
          this.total = data.total;
          this.totalShares = data?.totalShares;
          this.totalNomineeShares = data?.totalNomineeShares;
          this.nomineeSharesPercentage = (
            (data.totalNomineeShares / data.totalShares) *
            100
          ).toFixed(2);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  changePage(event?: PageEvent) {
    this.getTickerCodeList();
  }

  checkItemPrice(item: Business): string {
    if (!item.priceChg || item.priceChg == 0) return 'flat';
    else if (item.priceChg > 0) return 'increase';
    else return 'decrease';
  }

  absValue(item: number) {
    return Math.abs(item || 0);
  }

  openUploadModal() {
    const modalRef = this.modalService.open(GeneralDocumentUploadComponent, {
      centered: true,
      size: 'md',
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    });

    modalRef.componentInstance.document = {
      fileName: '',
      file: null,
      attachmentInfo: null,
      url: '',
    };

    modalRef.componentInstance.fileSizeLimit = 31457280; // 30MB

    modalRef.result
      .then((result) => {
        if (result && result.attachmentInfo) {
          this.isLoading = true;

          // Match the same rule the backend uses to resolve "the" general/other
          // document type (CanHaveMultiple + the issuer's chosen Audience) - do NOT
          // fall back to mysecAttachmentTypes[0]. That fallback used to grab whatever
          // type happened to sort first, which could be a single-instance type the
          // company already has a record for, causing a false "already exists"
          // error on a company's very first general-document upload.
          const audience: AttachmentAudience = result.audience;
          const attachmentType = this.mysecAttachmentTypes.find(
            (t) => t.canHaveMultiple && t.audience === audience,
          );
          if (!attachmentType) {
            this.isLoading = false;
            this.errorService.showError(
              'Unable to resolve the general document upload type. Please try again later.',
            );
            return;
          }

          const formData = new FormData();
          formData.append('type', attachmentType.id.toString());
          formData.append('status', '1');
          formData.append('audience', audience.toString());
          formData.append('documentName', result.fileName || '');

          formData.append('AttachmentId', result.attachmentInfo.id);
          formData.append('AttachmentInfo.Token', result.attachmentInfo.token);
          formData.append(
            'AttachmentInfo.EncryptedAttachmentInfo',
            result.attachmentInfo.encryptedAttachmentInfo,
          );

          this.businessesService
            .saveTickerDocument(formData)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
              next: () => {
                this.getTickerCodeList(true);
                this.getGeneralDocuments();
                this.modalService.open(SuccessPopupComponent, {
                  centered: true,
                  size: 'sm',
                  windowClass: 'modal-success-custom',
                });
              },
              error: (err) => {
                this.errorService.showError(err);
              },
            });
        }
      })
      .catch(() => {});
  }

  getGeneralDocuments() {
    if (!this.thisCompanyId) return;

    this.isLoading = true;

    this.businessesService
      .getGeneralDocuments(this.thisCompanyId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          const sections = res.data || [];
          const allDocuments: any[] = [];

          sections.forEach((section: any) => {
            if (section.documents) {
              section.documents.forEach((doc: any) => {
                allDocuments.push({
                  ...doc,
                  attachmentName: doc.attachmentName || doc.fileName || 'Untitled Document',
                  createdAt: doc.createdAt,
                  status: doc.status ?? doc.attachmentStatus ?? 1,
                  audience: doc.attachmentType?.audience ?? 0,
                });
              });
            }
          });

          this.documentsList = allDocuments.sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
          });
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  openUploadAnnouncementModal() {
    const companyId = this.thisCompanyId;
    if (!companyId) {
      this.errorService.showError('Unable to post announcement: company ID is missing.');
      return;
    }
    const modalRef = this.modalService.open(PostNewAnnouncementComponent, {
      centered: true,
      size: 'md',
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    });

    modalRef.componentInstance.companyId = companyId;
    modalRef.result.then(
      (result) => {
        if (result?.success) {
          // Refresh announcement list after successful creation
          this.getTickerCodeList();
          this.getAnnouncementList(true);
        }
      },
      () => {},
    );
  }

  getAnnouncementList(reload?: boolean) {
    if (reload) {
      this.announcementParams.currentPage = 1;
    }
    if (!this.thisCompanyId) return;

    this.businessesService
      .getCompanyDashboardAnnouncementByCompanyId(
        this.thisCompanyId,
        this.announcementParams.currentPage,
        this.announcementParams.take,
      )
      .subscribe({
        next: (data) => {
          this.announcementsList = data.data;
          this.announcementTotal = data.total;
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  getAudienceLabel(audience: TargetAudience): string {
    const audienceMap: Record<TargetAudience, string> = {
      [TargetAudience.All]: 'All',
      [TargetAudience.InvestorOnly]: 'Investor only',
    };

    return audience ? audienceMap[audience] : 'All';
  }

  get pagedDocuments() {
    const startIndex = (this.docPage - 1) * this.docPageSize;
    return this.documentsList?.slice(startIndex, startIndex + this.docPageSize) || [];
  }

  onPageChange(event: any) {
    this.docPage = event.pageIndex + 1;
    this.docPageSize = event.pageSize;
  }

  onAnnouncementPageChange(event: PageEvent) {
    this.announcementParams.currentPage = event.pageIndex + 1;
    this.announcementParams.take = event.pageSize;
    this.getAnnouncementList();
  }

  viewAnnouncement(announcement: any) {
    const modalRef = this.modalService.open(PostNewAnnouncementComponent, {
      centered: true,
      size: 'lg',
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    });

    modalRef.componentInstance.announcement = announcement;
    modalRef.componentInstance.isViewOnly = true;
    modalRef.result.then(
      () => {},
      () => {},
    );
  }

  viewDocument(doc: any) {
    if (doc && doc.url) {
      window.open(doc.url, '_blank');
      return;
    }

    if (doc.subAttachments && doc.subAttachments.length > 0) {
      const firstUrl = doc.subAttachments[0].url;
      if (firstUrl) {
        window.open(firstUrl, '_blank');
        return;
      }
    }

    this.errorService.showError('No uploaded file found for this document.');
  }

  deleteDocument(doc: any) {
    if (confirm(`Are you sure you want to delete "${doc.attachmentName}"?`)) {
      this.businessesService.deleteCompanyAttachment(doc.id).subscribe({
        next: (res) => {
          alert('Deleted successfully!');
          this.getGeneralDocuments();
        },
        error: (err) => {
          alert('Delete failed, please try again.');
        },
      });
    }
  }
}
